import { useEffect } from "react";
import { Link } from "react-router-dom";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import PageHeader from "@/components/PageHeader";
import useRevealOnScroll from "@/hooks/useRevealOnScroll";
import {
  ShieldCheck,
  Hourglass,
  Anchor,
  HandHeart,
  ArrowUpRight,
} from "lucide-react";

const values = [
  {
    icon: ShieldCheck,
    title: "Verlässlichkeit",
    text: "Klare Kommunikation, saubere Strukturen und verbindliches Handeln.",
    testId: "value-verlaesslichkeit",
  },
  {
    icon: Hourglass,
    title: "Langfristigkeit",
    text: "Kein kurzfristiger Hype, sondern nachhaltige Entwicklung.",
    testId: "value-langfristigkeit",
  },
  {
    icon: Anchor,
    title: "Bodenständigkeit",
    text: "Unternehmerische Nähe zur Praxis statt reiner Theorie.",
    testId: "value-bodenstaendigkeit",
  },
  {
    icon: HandHeart,
    title: "Verantwortung",
    text: "Gegenüber Partnern, Projekten, Mitarbeitern und regionalem Umfeld.",
    testId: "value-verantwortung",
  },
];

export default function Werte() {
  useRevealOnScroll();

  useEffect(() => {
    document.title = "Werte | Wagenhofer Capital";
  }, []);

  return (
    <div className="bg-black text-white min-h-screen flex flex-col">
      <Navigation />

      <PageHeader
        testId="page-values"
        overline="Werte"
        title={
          <>
            Werte, auf denen
            <br />
            <span className="text-zinc-400">wir aufbauen.</span>
          </>
        }
        subtitle="Vier Grundprinzipien, an denen sich jede Entscheidung, jedes Projekt und jede Partnerschaft messen lassen muss."
      />

      <section
        data-testid="section-values"
        className="section-y border-b border-white/5"
      >
        <div className="container-x">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 border-t border-l border-white/5">
            {values.map((value, idx) => {
              const Icon = value.icon;
              return (
                <div
                  key={value.title}
                  data-testid={value.testId}
                  className="group relative p-10 md:p-12 border-r border-b border-white/5 hover:bg-[#0a0a0a] transition-colors duration-500 reveal"
                  style={{ transitionDelay: `${idx * 70}ms` }}
                >
                  <div className="flex items-center justify-between mb-8">
                    <Icon className="w-6 h-6 text-[#D4AF37]" strokeWidth={1.25} />
                    <span className="overline text-zinc-600">
                      0{idx + 1}
                    </span>
                  </div>
                  <h3 className="font-display text-xl tracking-tight font-medium mb-4">
                    {value.title}
                  </h3>
                  <p className="text-zinc-400 leading-relaxed font-light text-sm">
                    {value.text}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="section-y">
        <div className="container-x flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
          <div className="reveal">
            <div className="overline mb-3">Nächster Schritt</div>
            <h3 className="font-display text-2xl md:text-3xl tracking-tight font-medium">
              Sie teilen unsere Werte? Schreiben Sie uns.
            </h3>
          </div>
          <Link
            to="/kontakt"
            data-testid="values-cta-contact"
            className="reveal delay-1 group inline-flex items-center gap-3 border border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black transition-all duration-300 px-8 py-4 uppercase tracking-[0.28em] text-[11px] font-semibold"
          >
            Kontakt aufnehmen
            <ArrowUpRight
              className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
              strokeWidth={1.5}
            />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
